import jsPDF from 'jspdf';
import { PDFDocument, PDFName, AFRelationship } from 'pdf-lib';
import {
  generateFacturXml,
  InvoiceXmlData,
  FACTUR_X_PROFILE,
  FACTUR_X_XML_FILENAME,
  vatFromSiret,
} from './facturXml';

export interface InvoiceItem {
  description: string;
  quantity: number;
  unitPrice: number;
  vatRate: number;
}

export interface InvoicePdfData {
  invoiceNumber: string;
  issueDate: string;
  dueDate?: string;
  sellerName: string;
  sellerEmail: string;
  sellerSiret: string;
  sellerVatNumber: string;
  sellerAddress: { street?: string; city?: string; postalCode?: string; country?: string };
  buyerName: string;
  buyerEmail: string;
  buyerAddress: { street?: string; city?: string; postalCode?: string; country?: string };
  items: InvoiceItem[];
  totalHt: number;
  totalVat: number;
  totalTtc: number;
  paymentMethod?: string;
  notes?: string;
  headerColor?: string;
  invoiceFooter?: string;
}

function hexToRgb(hex: string): [number, number, number] {
  const h = hex.replace('#', '');
  return [
    parseInt(h.slice(0, 2), 16),
    parseInt(h.slice(2, 4), 16),
    parseInt(h.slice(4, 6), 16),
  ];
}

const C = {
  primary: [26, 58, 58] as [number, number, number],
  accent: [0, 150, 136] as [number, number, number],
  text: [33, 33, 33] as [number, number, number],
  muted: [120, 120, 120] as [number, number, number],
  light: [240, 245, 244] as [number, number, number],
  border: [200, 215, 210] as [number, number, number],
  white: [255, 255, 255] as [number, number, number],
};

function fmtAddr(a: { street?: string; city?: string; postalCode?: string; country?: string }): string[] {
  const lines: string[] = [];
  if (a.street) lines.push(a.street);
  if (a.postalCode || a.city) lines.push([a.postalCode, a.city].filter(Boolean).join(' '));
  if (a.country && a.country !== 'FR') lines.push(a.country);
  return lines;
}

function xmlEsc(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;');
}

function isoDate(date: Date): string {
  return date.toISOString().replace(/\.\d{3}Z$/, 'Z');
}

function buildFacturXXmp(data: InvoicePdfData, createdAt: Date): string {
  const title = `Facture ${data.invoiceNumber}`;
  const description = `Facture ${data.invoiceNumber} emise par ${data.sellerName || 'Consentink'}`;
  const created = isoDate(createdAt);

  return `<?xpacket begin="﻿" id="W5M0MpCehiHzreSzNTczkc9d"?>
<x:xmpmeta xmlns:x="adobe:ns:meta/">
  <rdf:RDF xmlns:rdf="http://www.w3.org/1999/02/22-rdf-syntax-ns#">
    <rdf:Description rdf:about="" xmlns:pdfaid="http://www.aiim.org/pdfa/ns/id/" pdfaid:part="3" pdfaid:conformance="B"/>
    <rdf:Description rdf:about="" xmlns:dc="http://purl.org/dc/elements/1.1/">
      <dc:format>application/pdf</dc:format>
      <dc:title><rdf:Alt><rdf:li xml:lang="x-default">${xmlEsc(title)}</rdf:li></rdf:Alt></dc:title>
      <dc:description><rdf:Alt><rdf:li xml:lang="x-default">${xmlEsc(description)}</rdf:li></rdf:Alt></dc:description>
      <dc:creator><rdf:Seq><rdf:li>${xmlEsc(data.sellerName || 'Consentink')}</rdf:li></rdf:Seq></dc:creator>
    </rdf:Description>
    <rdf:Description rdf:about="" xmlns:xmp="http://ns.adobe.com/xap/1.0/">
      <xmp:CreatorTool>Consentink Factur-X</xmp:CreatorTool>
      <xmp:CreateDate>${created}</xmp:CreateDate>
      <xmp:ModifyDate>${created}</xmp:ModifyDate>
      <xmp:MetadataDate>${created}</xmp:MetadataDate>
    </rdf:Description>
    <rdf:Description rdf:about="" xmlns:pdf="http://ns.adobe.com/pdf/1.3/">
      <pdf:Producer>Consentink Factur-X</pdf:Producer>
    </rdf:Description>
    <rdf:Description rdf:about="" xmlns:fx="urn:factur-x:pdfa:CrossIndustryDocument:invoice:1p0#">
      <fx:DocumentType>INVOICE</fx:DocumentType>
      <fx:DocumentFileName>${FACTUR_X_XML_FILENAME}</fx:DocumentFileName>
      <fx:Version>1.0</fx:Version>
      <fx:ConformanceLevel>${FACTUR_X_PROFILE}</fx:ConformanceLevel>
    </rdf:Description>
    <rdf:Description rdf:about="" xmlns:pdfaExtension="http://www.aiim.org/pdfa/ns/extension/" xmlns:pdfaSchema="http://www.aiim.org/pdfa/ns/schema#" xmlns:pdfaProperty="http://www.aiim.org/pdfa/ns/property#">
      <pdfaExtension:schemas>
        <rdf:Bag>
          <rdf:li rdf:parseType="Resource">
            <pdfaSchema:schema>Factur-X PDFA Extension Schema</pdfaSchema:schema>
            <pdfaSchema:namespaceURI>urn:factur-x:pdfa:CrossIndustryDocument:invoice:1p0#</pdfaSchema:namespaceURI>
            <pdfaSchema:prefix>fx</pdfaSchema:prefix>
            <pdfaSchema:property>
              <rdf:Seq>
                <rdf:li rdf:parseType="Resource"><pdfaProperty:name>DocumentFileName</pdfaProperty:name><pdfaProperty:valueType>Text</pdfaProperty:valueType><pdfaProperty:category>external</pdfaProperty:category><pdfaProperty:description>The name of the embedded XML document</pdfaProperty:description></rdf:li>
                <rdf:li rdf:parseType="Resource"><pdfaProperty:name>DocumentType</pdfaProperty:name><pdfaProperty:valueType>Text</pdfaProperty:valueType><pdfaProperty:category>external</pdfaProperty:category><pdfaProperty:description>The type of the hybrid document</pdfaProperty:description></rdf:li>
                <rdf:li rdf:parseType="Resource"><pdfaProperty:name>Version</pdfaProperty:name><pdfaProperty:valueType>Text</pdfaProperty:valueType><pdfaProperty:category>external</pdfaProperty:category><pdfaProperty:description>The version of the Factur-X standard</pdfaProperty:description></rdf:li>
                <rdf:li rdf:parseType="Resource"><pdfaProperty:name>ConformanceLevel</pdfaProperty:name><pdfaProperty:valueType>Text</pdfaProperty:valueType><pdfaProperty:category>external</pdfaProperty:category><pdfaProperty:description>The conformance level of the embedded XML document</pdfaProperty:description></rdf:li>
              </rdf:Seq>
            </pdfaSchema:property>
          </rdf:li>
        </rdf:Bag>
      </pdfaExtension:schemas>
    </rdf:Description>
  </rdf:RDF>
</x:xmpmeta>
<?xpacket end="w"?>`;
}

function renderInvoiceLayout(data: InvoicePdfData): jsPDF {
  const doc = new jsPDF();
  const pw = doc.internal.pageSize.getWidth();
  const m = 16;
  const cw = pw - m * 2;
  let y = 0;

  const primaryRgb = hexToRgb(data.headerColor || '#1a3a3a');

  // Header band
  doc.setFillColor(...primaryRgb);
  doc.rect(0, 0, pw, 36, 'F');
  doc.setFontSize(20);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...C.white);
  doc.text('FACTURE', m, 16);
  doc.setFontSize(12);
  doc.setFont('helvetica', 'normal');
  doc.text(data.invoiceNumber, m, 28);

  doc.setFontSize(8);
  doc.setFillColor(...C.accent);
  doc.roundedRect(pw - m - 32, 8, 32, 10, 2, 2, 'F');
  doc.setTextColor(...C.white);
  doc.text('Factur-X', pw - m - 16, 15, { align: 'center' });

  // Dates
  y = 46;
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...C.muted);
  doc.text(`Date d'emission : ${data.issueDate}`, m, y);
  if (data.dueDate) doc.text(`Date d'echeance : ${data.dueDate}`, m + cw / 2, y);
  y += 10;

  // Seller card
  const colW = cw / 2 - 4;
  doc.setFillColor(...C.light);
  doc.roundedRect(m, y, colW, 42, 2, 2, 'F');
  doc.setFontSize(8);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...C.accent);
  doc.text('EMETTEUR', m + 4, y + 7);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...C.text);
  doc.setFontSize(9);
  let sy = y + 14;
  doc.text(data.sellerName, m + 4, sy); sy += 5;
  for (const line of fmtAddr(data.sellerAddress)) { doc.text(line, m + 4, sy); sy += 4.5; }
  if (data.sellerSiret) {
    doc.setFontSize(7.5);
    doc.setTextColor(...C.muted);
    doc.text(`SIRET: ${data.sellerSiret}`, m + 4, sy);
    sy += 4;
  }
  const displayedVat = data.sellerVatNumber || vatFromSiret(data.sellerSiret) || '';
  if (displayedVat) { doc.text(`TVA: ${displayedVat}`, m + 4, sy); }

  // Buyer card
  const bx = m + colW + 8;
  doc.setFillColor(...C.light);
  doc.roundedRect(bx, y, colW, 42, 2, 2, 'F');
  doc.setFontSize(8);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...C.accent);
  doc.text('CLIENT', bx + 4, y + 7);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...C.text);
  doc.setFontSize(9);
  let by = y + 14;
  doc.text(data.buyerName, bx + 4, by); by += 5;
  for (const line of fmtAddr(data.buyerAddress)) { doc.text(line, bx + 4, by); by += 4.5; }
  if (data.buyerEmail) {
    doc.setFontSize(7.5);
    doc.setTextColor(...C.muted);
    doc.text(data.buyerEmail, bx + 4, by);
  }

  y += 50;

  // Table header
  const cols = [
    { label: 'Description', x: m,             w: cw * 0.42 },
    { label: 'Qte',         x: m + cw * 0.42, w: cw * 0.10 },
    { label: 'P.U. HT',    x: m + cw * 0.52, w: cw * 0.16 },
    { label: 'TVA %',       x: m + cw * 0.68, w: cw * 0.12 },
    { label: 'Total HT',    x: m + cw * 0.80, w: cw * 0.20 },
  ];

  doc.setFillColor(...primaryRgb);
  doc.rect(m, y, cw, 8, 'F');
  doc.setFontSize(8);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...C.white);
  for (const col of cols) doc.text(col.label, col.x + 2, y + 5.5);
  y += 8;

  // Table rows
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...C.text);
  doc.setFontSize(8.5);
  for (let i = 0; i < data.items.length; i++) {
    const item = data.items[i];
    const lineTotal = item.quantity * item.unitPrice;
    if (i % 2 === 0) {
      doc.setFillColor(...C.light);
      doc.rect(m, y, cw, 7, 'F');
    }
    doc.setTextColor(...C.text);
    const descLines = doc.splitTextToSize(item.description, cols[0].w - 4);
    doc.text(descLines[0], cols[0].x + 2, y + 5);
    doc.text(String(item.quantity), cols[1].x + 2, y + 5);
    doc.text(`${item.unitPrice.toFixed(2)} EUR`, cols[2].x + 2, y + 5);
    doc.text(`${item.vatRate.toFixed(0)}%`, cols[3].x + 2, y + 5);
    doc.text(`${lineTotal.toFixed(2)} EUR`, cols[4].x + 2, y + 5);
    y += 7;
  }

  // Separator
  doc.setDrawColor(...C.border);
  doc.setLineWidth(0.3);
  doc.line(m, y, m + cw, y);
  y += 6;

  // Totals block
  const totX = m + cw * 0.6;
  const totW = cw * 0.4;
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...C.text);
  doc.text('Total HT', totX, y);
  doc.text(`${data.totalHt.toFixed(2)} EUR`, totX + totW - 2, y, { align: 'right' });
  y += 6;
  doc.text('TVA', totX, y);
  doc.text(`${data.totalVat.toFixed(2)} EUR`, totX + totW - 2, y, { align: 'right' });
  y += 6;

  doc.setFillColor(...C.accent);
  doc.roundedRect(totX - 2, y - 4, totW + 4, 10, 2, 2, 'F');
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...C.white);
  doc.setFontSize(11);
  doc.text('Total TTC', totX + 2, y + 3);
  doc.text(`${data.totalTtc.toFixed(2)} EUR`, totX + totW, y + 3, { align: 'right' });
  y += 16;

  // Payment / conditions
  if (data.paymentMethod && data.paymentMethod !== 'cash') {
    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...C.muted);
    doc.text(`Mode de paiement : ${data.paymentMethod}`, m, y);
    y += 6;
  }
  if (!data.dueDate) {
    doc.setFontSize(8);
    doc.setTextColor(...C.muted);
    doc.text('Conditions : Paiement a reception de facture', m, y);
    y += 6;
  }
  if (data.notes) {
    doc.setFontSize(8);
    doc.setTextColor(...C.muted);
    const noteLines = doc.splitTextToSize(data.notes, cw);
    doc.text(noteLines, m, y);
  }

  // Footer
  const footerText = data.invoiceFooter?.trim() || '';
  if (footerText) {
    doc.setFontSize(7);
    doc.setTextColor(...C.muted);
    const footerLines = doc.splitTextToSize(footerText, cw);
    doc.text(footerLines, pw / 2, doc.internal.pageSize.getHeight() - 10, { align: 'center' });
  }

  return doc;
}

async function postProcessPdfA3(
  pdfBytes: Uint8Array,
  xmlBytes: Uint8Array,
  xmpString: string,
  createdAt: Date,
  data: InvoicePdfData,
): Promise<Uint8Array> {
  const pdfDoc = await PDFDocument.load(pdfBytes);

  pdfDoc.setTitle(`Facture ${data.invoiceNumber}`);
  pdfDoc.setSubject(`Facture ${data.invoiceNumber}`);
  pdfDoc.setAuthor(data.sellerName || 'Consentink');
  pdfDoc.setProducer('Consentink Factur-X');
  pdfDoc.setCreator('Consentink Factur-X');
  pdfDoc.setCreationDate(createdAt);
  pdfDoc.setModificationDate(createdAt);
  pdfDoc.setKeywords(['factur-x', 'EN16931', data.invoiceNumber]);

  // Attach the Factur-X XML (critical for machine-readable invoices)
  await pdfDoc.attach(xmlBytes, FACTUR_X_XML_FILENAME, {
    mimeType: 'text/xml',
    description: 'Factur-X EN 16931 invoice',
    creationDate: createdAt,
    modificationDate: createdAt,
    afRelationship: AFRelationship.Alternative,
  });

  const context = pdfDoc.context;
  const catalog = pdfDoc.catalog;

  // Embed XMP metadata (Factur-X requirement)
  const xmpBytes = new TextEncoder().encode(xmpString);
  const xmpStream = context.stream(xmpBytes, {
    Type: 'Metadata',
    Subtype: 'XML',
    Length: xmpBytes.length,
  });
  const xmpRef = context.register(xmpStream);
  catalog.set(PDFName.of('Metadata'), xmpRef);

  return pdfDoc.save({ useObjectStreams: false });
}

export async function generateInvoicePdf(
  data: InvoicePdfData,
): Promise<{ pdfBlob: Blob; xmlString: string }> {
  const createdAt = new Date();
  const doc = renderInvoiceLayout(data);

  const xmlData: InvoiceXmlData = {
    invoiceNumber: data.invoiceNumber,
    issueDate: data.issueDate,
    dueDate: data.dueDate,
    sellerName: data.sellerName,
    sellerSiret: data.sellerSiret,
    sellerVatNumber: data.sellerVatNumber,
    sellerAddress: data.sellerAddress,
    sellerEmail: data.sellerEmail,
    buyerName: data.buyerName,
    buyerEmail: data.buyerEmail,
    buyerAddress: data.buyerAddress,
    items: data.items,
    totalHt: data.totalHt,
    totalVat: data.totalVat,
    totalTtc: data.totalTtc,
    paymentMethod: data.paymentMethod,
    notes: data.notes,
  };

  const xmlString = generateFacturXml(xmlData);
  const xmlBytes = new TextEncoder().encode(xmlString);
  const baseBytes = new Uint8Array(doc.output('arraybuffer'));
  const xmpString = buildFacturXXmp(data, createdAt);
  const finalBytes = await postProcessPdfA3(baseBytes, xmlBytes, xmpString, createdAt, data);

  return {
    pdfBlob: new Blob([finalBytes as BlobPart], { type: 'application/pdf' }),
    xmlString,
  };
}

export function calculateTotals(
  items: InvoiceItem[],
): { totalHt: number; totalVat: number; totalTtc: number } {
  let totalHt = 0;
  let totalVat = 0;
  for (const item of items) {
    const lineHt = item.quantity * item.unitPrice;
    totalHt += lineHt;
    totalVat += lineHt * (item.vatRate / 100);
  }
  return {
    totalHt: Math.round(totalHt * 100) / 100,
    totalVat: Math.round(totalVat * 100) / 100,
    totalTtc: Math.round((totalHt + totalVat) * 100) / 100,
  };
}
